import { useState, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';
import ThemeMaker from "../../components/thememaker";
import { useParams } from "react-router-dom";
import { registerSeller } from "../../store/features/seller/sellerSlice";
import { useSelector, useDispatch } from "react-redux";
import { config } from "../../utils/config";
import { toast } from "react-toastify";
import axios from "axios";
import "../../index.css"
const { apiEndpoint, awsKeyId, awsSecretKey, awsRegion, awsBucket, baseNodeapiUrl } = config
const aws = require('aws-sdk');

interface ICustomer {
    id: any;
}

// Fixed domain value expected by the create API
const FIXED_DOMAIN = "ONDC:RETeB2B";

const AddSeller = () => {
    const dispatch = useDispatch<any>();
    const _sellerData = useSelector((state: any) => state.sellerReducer);

    let params: ICustomer = useParams();

    const STORAGE_KEY = "add_seller_draft_data";

    const getStoredDraft = () => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error("Error reading stored seller draft:", e);
        }
        return null;
    };

    const initialDraft = getStoredDraft();

    // Step control: 1 = verify (name/contact/gst), 2 = seller details (email/type/address proof)
    const [step, setStep] = useState<1 | 2>(initialDraft?.step === 2 && initialDraft?.isVerified ? 2 : 1);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isVerified, setIsVerified] = useState(Boolean(initialDraft?.isVerified));

    const [isgstValid, setisgstValid] = useState(true);

    const rawVerifyData = {
        name: '',
        contact_no: '',
        gst_no: ''
    };

    const rawSellerData = {
        seller_id: '',
        company_name: '',
        pan_no: '',
        email: '',
        seller_type: '',
        address_proof: '',
        address_proof_image: ''
    };

    const rawErrorData = {
        name: false,
        contact_no: false,
        gst_no: false,
        email: false,
        seller_type: false,
        address_proof: false,
        address_proof_image: false
    };

    const [verifyData, setVerifyData]: any = useState(initialDraft?.verifyData ? { ...rawVerifyData, ...initialDraft.verifyData } : rawVerifyData);
    const [sellerData, setSellerData]: any = useState(initialDraft?.sellerData ? { ...rawSellerData, ...initialDraft.sellerData } : rawSellerData);
    const [sellerDataError, setSellerDataError]: any = useState(rawErrorData);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingProof, setIsUploadingProof] = useState(false);

    const handleResetAll = () => {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error("Error clearing draft:", e);
        }
        setStep(1);
        setIsVerifying(false);
        setIsVerified(false);
        setisgstValid(true);
        setVerifyData(rawVerifyData);
        setSellerData(rawSellerData);
        setSellerDataError(rawErrorData);
    };

    // Listen for reset events triggered from sidebar navigation
    useEffect(() => {
        const handleResetEvent = () => {
            handleResetAll();
        };
        window.addEventListener("reset_add_seller", handleResetEvent);
        return () => {
            window.removeEventListener("reset_add_seller", handleResetEvent);
        };
    }, []);

    // Save to sessionStorage whenever state changes
    useEffect(() => {
        try {
            if (isVerified || step === 2 || verifyData.name || verifyData.contact_no || verifyData.gst_no) {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                    step,
                    isVerified,
                    verifyData,
                    sellerData
                }));
            } else {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        } catch (e) {
            console.error("Error writing stored seller draft:", e);
        }
    }, [step, isVerified, verifyData, sellerData]);

    const verifyInputHandler = (e: any) => {
        setVerifyData({ ...verifyData, [e.target.name]: e.target.value });
    };

    const inputHandler = (e: any) => {
        setSellerData({ ...sellerData, [e.target.name]: e.target.value });
    };

    const errorHandler = (e: any) => {
        const gstregex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (e.target.value !== "" && e.target.name === "gst_no") {
            if (gstregex.test(e.target.value)) {
                setisgstValid(true);
            } else {
                setisgstValid(false);
            }
        }

        if (e.target.name === "email") {
            const trimmed = e.target.value.trim();
            if (!trimmed) {
                setSellerDataError({ ...sellerDataError, email: true });
            } else if (!emailRegex.test(trimmed)) {
                setSellerDataError({ ...sellerDataError, email: "invalid" });
            } else {
                setSellerDataError({ ...sellerDataError, email: "valid" });
            }
            return;
        }

        if (e.target.value) {
            setSellerDataError({ ...sellerDataError, [e.target.name]: "valid" });
        } else {
            setSellerDataError({ ...sellerDataError, [e.target.name]: true });
        }
    };

    // ---- STEP 1: VERIFY (Name + Contact + GST) ----
    const handleVerify = async () => {
        const nameRegex = /^[a-zA-Z\s.'-]+$/;
        const contactRegex = /^[0-9]{10}$/;
        const gstregex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

        let newErrors: any = { ...sellerDataError };
        let hasError = false;

        if (!verifyData.name || !verifyData.name.trim() || !nameRegex.test(verifyData.name.trim())) {
            newErrors.name = !verifyData.name || !verifyData.name.trim() ? true : "invalid";
            hasError = true;
        } else {
            newErrors.name = "valid";
        }

        if (!verifyData.contact_no || !contactRegex.test(verifyData.contact_no.toString().trim())) {
            newErrors.contact_no = true;
            hasError = true;
        } else {
            newErrors.contact_no = "valid";
        }

        if (!verifyData.gst_no || !gstregex.test(verifyData.gst_no.trim())) {
            newErrors.gst_no = true;
            setisgstValid(false);
            hasError = true;
        } else {
            newErrors.gst_no = "valid";
            setisgstValid(true);
        }

        setSellerDataError(newErrors);

        if (hasError) {
            toast.error("Please fill Name, Contact Number and a valid GST number!");
            return;
        }

        setIsVerifying(true);
        try {
            const token = sessionStorage.getItem("token");
            const { data } = await axios({
                method: "POST",
                url: `${apiEndpoint}/snp/verify/gst-kyc`,
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`
                },
                data: {
                    name: verifyData.name.trim(),
                    contact_no: verifyData.contact_no.toString().trim(),
                    gst_no: verifyData.gst_no.trim().toUpperCase()
                }
            });

            const isSuccess = data?.status === true || data?.status === 200 || data?.success === true;

            if (isSuccess) {
                const seller = data?.data?.seller || {};
                const gstDetails = data?.data?.gst_details || {};

                const fetchedSellerId = seller?.id || "";
                const fetchedCompanyName = seller?.company_name || "";
                const fetchedPan = gstDetails?.pan_no || "";

                setSellerData((prev: any) => ({
                    ...prev,
                    seller_id: fetchedSellerId,
                    company_name: fetchedCompanyName,
                    pan_no: fetchedPan
                }));

                setisgstValid(true);
                toast.success(data?.message || "GST verified successfully!");
                setIsVerified(true);
                setStep(2);
            } else {
                setisgstValid(false);
                toast.error(data?.message || "GST verification failed. Please check the details.");
            }
        } catch (err: any) {
            console.log("GST Verification Error", err);
            const msg = err?.response?.data?.message || err?.response?.data?.error || "GST verification failed. Please try again.";
            toast.error(msg);
        } finally {
            setIsVerifying(false);
        }
    };

    // ---- Image upload (used for Address Proof Image) ----
    const ImageHandler = async (e: any) => {
        const files = e.target.files;

        if (!files || files.length === 0) {
            return null;
        }

        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append("files", files[i]);
        }

        try {
            const { data } = await axios.post(
                `${apiEndpoint}/snp/upload`,
                formData,
                {
                    headers: {
                        "x-custom-authorization": `Bearer ${sessionStorage.getItem('token')}`,
                    }
                }
            );

            if (data?.success) {
                toast.success(data.message || "Upload successful!");
                return data.urls[0];
            } else {
                toast.error("Upload failed!");
                return null;
            }
        } catch (err) {
            console.error("Upload failed:", err);
            toast.error("Upload failed!");
            return null;
        }
    };

    const MAX_IMAGE_SIZE = 4 * 1024 * 1024; // 4MB
    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    const setAddressProofImage = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check file type (no PDF, only PNG, JPG, JPEG, WEBP)
        if (!ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
            toast.error("Invalid file format! Only images (PNG, JPG, JPEG, WEBP) are allowed. PDF is not allowed.");
            e.target.value = "";
            return;
        }

        // Check file size (max 4 MB)
        if (file.size > MAX_IMAGE_SIZE) {
            toast.error("Image size exceeds 4MB! Please upload an image under 4MB.");
            e.target.value = "";
            return;
        }

        setIsUploadingProof(true);
        const url = await ImageHandler(e);
        if (url) {
            setSellerData((prev: any) => ({ ...prev, address_proof_image: url }));
            setSellerDataError((prev: any) => ({ ...prev, address_proof_image: "valid" }));
        }
        setIsUploadingProof(false);
    };

    // ---- STEP 2: CREATE SELLER (Email + Seller Type + Address Proof + Address Proof Image) ----
    const newUserCreatehandler = async () => {
        const { seller_id, email, seller_type, address_proof, address_proof_image } = sellerData;

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        let newErrors: any = { ...sellerDataError };
        let hasError = false;

        if (!email || !email.trim()) {
            newErrors.email = true;
            hasError = true;
        } else if (!emailRegex.test(email.trim())) {
            newErrors.email = "invalid";
            hasError = true;
        } else {
            newErrors.email = "valid";
        }

        if (!seller_type || !seller_type.trim()) {
            newErrors.seller_type = true;
            hasError = true;
        } else {
            newErrors.seller_type = "valid";
        }

        if (!address_proof || !address_proof.trim()) {
            newErrors.address_proof = true;
            hasError = true;
        } else {
            newErrors.address_proof = "valid";
        }

        if (!address_proof_image) {
            newErrors.address_proof_image = true;
            hasError = true;
        } else {
            newErrors.address_proof_image = "valid";
        }

        setSellerDataError(newErrors);

        if (hasError) {
            toast.error("Please fill all required fields correctly!");
            return;
        }

        if (!seller_id) {
            toast.error("Missing seller reference from verification step. Please verify again.");
            setStep(1);
            return;
        }

        const payload = {
            seller_id: seller_id,
            email: email.trim(),
            address_proof: address_proof.trim(),
            address_proof_image: address_proof_image,
            seller_type: seller_type.trim(),
            domain: [FIXED_DOMAIN]
        };

        const token = sessionStorage.getItem("token");
        setIsLoading(true);

        try {
            const response = await axios({
                url: `${apiEndpoint}/snp/sellers/create`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`
                },
                data: payload
            });

            const resData = response?.data;
            if (resData?.status === true || resData?.status === 200 || resData?.success === true || response?.status === 200) {
                toast.success(resData?.message || "Seller registered successfully!");
                try {
                    sessionStorage.removeItem(STORAGE_KEY);
                } catch (e) {
                    console.error("Error removing stored seller draft:", e);
                }
                setVerifyData(rawVerifyData);
                setSellerData(rawSellerData);
                setSellerDataError(rawErrorData);
                setStep(1);
                setIsVerified(false);
            } else {
                toast.error(resData?.message || resData?.error || "Failed to register seller!");
            }
        } catch (error: any) {
            console.error("Register Seller API Error:", error);
            const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Failed to register seller!";
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {

    }, []);

    return (
        <>
            <ThemeMaker>
                <div className="row m-0 w-100 mt-1">
                    <div className="col-md-12">
                        <div className=" mb-4">
                            <div className="card-body" style={{ height: "80vh", overflow: "scroll", scrollbarWidth: "none" }}>

                                {/* ---------------- STEP 1: VERIFY ---------------- */}
                                {step === 1 && (
                                    <div className="row">
                                        <div className="col-md-12 mb-3">
                                            <h5 className="font-weight-bold">Seller Verification</h5>
                                            <p style={{ fontSize: "13px", color: "#64748B" }}>
                                                Enter the seller's name, contact number and GST number to verify before proceeding.
                                            </p>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">
                                                    Name
                                                    <span className="text-danger"> * </span>
                                                    {
                                                        sellerDataError?.name !== "valid" && sellerDataError?.name !== false ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : null
                                                    }
                                                </label>
                                                <input type="text" className="form-control" name="name" placeholder="Enter name"
                                                    value={verifyData.name}
                                                    onChange={(e) => verifyInputHandler(e)}
                                                    onBlur={(e) => errorHandler(e)}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">Contact No.<span className="text-danger">*</span>
                                                    {
                                                        sellerDataError?.contact_no !== "valid" && sellerDataError?.contact_no !== false ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : null
                                                    }
                                                </label>
                                                <input type="text" name="contact_no" className="form-control" placeholder="Enter contact number (10 digits)"
                                                    maxLength={10}
                                                    value={verifyData.contact_no}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                                        setVerifyData({ ...verifyData, contact_no: val });
                                                    }}
                                                    onBlur={(e) => errorHandler(e)}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">GST<span className="text-danger">*</span>
                                                    {
                                                        sellerDataError?.gst_no !== "valid" && sellerDataError?.gst_no !== false ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : null
                                                    }
                                                    {
                                                        isgstValid ? null : <span className="text-danger tiny_size"> The GST you have entered is invalid ! </span>
                                                    }
                                                </label>
                                                <input type="text" className="form-control" name="gst_no" placeholder="Enter GST number"
                                                    value={verifyData.gst_no}
                                                    onChange={(e) => verifyInputHandler(e)}
                                                    onBlur={(e) => errorHandler(e)}
                                                    onFocus={() => setisgstValid(true)}
                                                    maxLength={15}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-12 d-flex justify-content-center mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                style={{ width: "200px", borderRadius: "20px" }}
                                                disabled={isVerifying}
                                                onClick={handleVerify}
                                            >
                                                {isVerifying ? "Verifying..." : "Verify & Save"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* ---------------- STEP 2: SELLER CREATE DETAILS ---------------- */}
                                {step === 2 && (
                                    <div className="row">
                                        <div className="col-md-12 mb-3">
                                            <div style={{ background: "#F8FAFC", padding: 14, borderRadius: 8, border: "1px solid #E2E8F0", display: "flex", gap: "24px", flexWrap: "wrap" }}>
                                                <div>
                                                    <span style={{ fontSize: 11, color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>Company Name</span>
                                                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{sellerData.company_name || "N/A"}</div>
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: 11, color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>PAN Number</span>
                                                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{sellerData.pan_no || "N/A"}</div>
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: 11, color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>Domain</span>
                                                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{FIXED_DOMAIN}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Company Name - read-only, non-editable */}
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">Company Name</label>
                                                <input type="text" className="form-control" name="company_name"
                                                    value={sellerData.company_name || "N/A"}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/* PAN Number - read-only, non-editable */}
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">PAN Number</label>
                                                <input type="text" className="form-control" name="pan_no"
                                                    value={sellerData.pan_no || "N/A"}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">Email address<span className="text-danger">*</span>
                                                    {
                                                        sellerDataError?.email === true ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : sellerDataError?.email === "invalid" ? (
                                                            <span className="text-danger tiny_size"> Please enter a valid email address ! </span>
                                                        ) : null
                                                    }
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    className="form-control"
                                                    placeholder="Enter email (e.g. name@domain.com)"
                                                    value={sellerData.email}
                                                    onKeyDown={(e) => {
                                                        if (e.key === " " || e.code === "Space") {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const val = e.target.value.trimStart().replace(/\s+/g, "").replace(/[^a-zA-Z0-9@._\-+]/g, "");
                                                        setSellerData({ ...sellerData, email: val });
                                                    }}
                                                    onBlur={(e) => errorHandler(e)}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">Seller Type<span className="text-danger">*</span>
                                                    {
                                                        sellerDataError?.seller_type !== "valid" && sellerDataError?.seller_type !== false ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : null
                                                    }
                                                </label>
                                                <select name="seller_type" className="form-control"
                                                    value={sellerData.seller_type}
                                                    onChange={(e) => inputHandler(e)}
                                                    onBlur={(e) => errorHandler(e)}
                                                >
                                                    <option value="">Select seller type</option>
                                                    <option value="Wholesaler">Wholesaler</option>
                                                    <option value="Manufacturer">Manufacturer</option>
                                                    <option value="Distributor">Distributor</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">Address Proof<span className="text-danger">*</span>
                                                    {
                                                        sellerDataError?.address_proof !== "valid" && sellerDataError?.address_proof !== false ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : null
                                                    }
                                                </label>
                                                <select name="address_proof" className="form-control"
                                                    value={sellerData.address_proof}
                                                    onChange={(e) => inputHandler(e)}
                                                    onBlur={(e) => errorHandler(e)}
                                                >
                                                    <option value="">Select address proof</option>
                                                    <option value="aadhar_card">Aadhar card</option>
                                                    <option value="ration_card">Ration card</option>
                                                    <option value="telephone_bill">Telephone bill</option>
                                                    <option value="bank_passbook">Bank Passbook</option>
                                                    <option value="electricity_bill">Electricity bill</option>
                                                    <option value="lease_or_license_agreement">Lease or license agreement</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="genericlabel">Address Proof Image<span className="text-danger">*</span>
                                                    {
                                                        sellerDataError?.address_proof_image !== "valid" && sellerDataError?.address_proof_image !== false ? (
                                                            <span className="text-danger tiny_size"> This field is required ! </span>
                                                        ) : null
                                                    }
                                                </label>
                                                <input
                                                    type="file"
                                                    name="address_proof_image"
                                                    className="form-control"
                                                    accept="image/png, image/jpeg, image/jpg, image/webp"
                                                    onChange={(e) => setAddressProofImage(e)}
                                                />
                                                <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap" style={{ gap: "6px" }}>
                                                    <span style={{ fontSize: "11px", color: "#64748B" }}>
                                                        PNG, JPG, JPEG, WEBP files supported (Max: 4MB). PDF not allowed.
                                                    </span>
                                                    {isUploadingProof && (
                                                        <span className="tiny_size font-weight-bold" style={{ color: "#3B82F6" }}>
                                                            <i className="fas fa-spinner fa-spin mr-1"></i> Uploading...
                                                        </span>
                                                    )}
                                                    {sellerData.address_proof_image && !isUploadingProof && (
                                                        <span className="tiny_size font-weight-bold" style={{ color: "#16A34A" }}>
                                                            <i className="fas fa-check-circle mr-1"></i> Uploaded ✓
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Image Preview Thumbnail */}
                                                {sellerData.address_proof_image && (
                                                    <div className="mt-2 d-flex align-items-center" style={{ gap: "10px" }}>
                                                        <a href={sellerData.address_proof_image} target="_blank" rel="noopener noreferrer">
                                                            <img
                                                                src={sellerData.address_proof_image}
                                                                alt="Address Proof Preview"
                                                                style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "6px", border: "1px solid #CBD5E1" }}
                                                            />
                                                        </a>
                                                        <a
                                                            href={sellerData.address_proof_image}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ fontSize: "12px", color: "#2563EB", fontWeight: 600, textDecoration: "underline" }}
                                                        >
                                                            View uploaded image
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-12 d-flex justify-content-center gap-2 mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                style={{ width: "150px", borderRadius: "20px", marginRight: "12px" }}
                                                disabled={isLoading || _sellerData?.loading}
                                                onClick={() => setStep(1)}
                                            >
                                                Back
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                style={{ width: "200px", borderRadius: "20px" }}
                                                disabled={isLoading || _sellerData?.loading || isUploadingProof}
                                                onClick={newUserCreatehandler}
                                            >
                                                {
                                                    isLoading || _sellerData?.loading ? 'Saving...' : "Update Details"
                                                }
                                            </button>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </ThemeMaker>
        </>
    )
}

export default AddSeller;